import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Combobox, Group, Pill, PillsInput, useCombobox } from '@mantine/core';
import { t } from 'i18next';
import { ITherapeuticIndication } from '../../../global/entities/TherapeuticIndication/TherapeuticIndication';

export type ITherapeuticIndicationMultiSelectProps = {
    value?: number[];
    onChange: (values: number[]) => void;
    isError: boolean;
    therapeuticIndicationsData?: ITherapeuticIndication[];
    placeholder?: string;
};

const TherapeuticIndicationMultiSelect: React.FC<ITherapeuticIndicationMultiSelectProps> = inject()(
    observer(({ value = [], onChange, isError, therapeuticIndicationsData = [], placeholder }) => {
        const [search, setSearch] = useState('');

        const combobox = useCombobox({
            onDropdownClose: () => combobox.resetSelectedOption(),
            onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
        });

        const handleToggleIndication = (indicationId: number) => {
            if (value.includes(indicationId)) 
                onChange(value.filter(id => id !== indicationId));
             else 
                onChange([...value, indicationId]);
            
        };

        const handleValueRemove = (val: string) => {
            onChange(value.filter(id => id !== parseInt(val)));
        };

        const filteredOptions = useMemo(() => {
            return therapeuticIndicationsData.filter(indication =>
                indication.name.toLowerCase().includes(search.toLowerCase())
            );
        }, [therapeuticIndicationsData, search]);

        const pills = value.map((selectedId) => {
            const indication = therapeuticIndicationsData.find(ti => ti.id === selectedId);
            if (!indication) return null;
            
            return (
                <Pill key={selectedId} withRemoveButton onRemove={() => handleValueRemove(selectedId.toString())}>
                    {indication.name}
                </Pill>
            );
        }).filter(Boolean);

        const options = filteredOptions.map((indication) => {
            const isSelected = value.includes(indication.id);
            return (
                <Combobox.Option value={indication.id.toString()} key={indication.id}>
                    <Group gap="sm">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ pointerEvents: 'none' }}
                        />
                        <span>{indication.name}</span>
                        {indication.description && (
                            <span style={{ fontSize: '0.8em', color: 'gray' }}>
                                {indication.description}
                            </span>
                        )}
                    </Group>
                </Combobox.Option>
            );
        });

        return (
            <Combobox
                store={combobox}
                onOptionSubmit={(val) => {
                    handleToggleIndication(parseInt(val));
                    setSearch('');
                }}
            >
                <Combobox.DropdownTarget>
                    <PillsInput onClick={() => combobox.openDropdown()} error={isError}>
                        <Pill.Group>
                            {pills}
                            <Combobox.EventsTarget>
                                <PillsInput.Field
                                    placeholder={placeholder}
                                    value={search}
                                    onChange={(event) => {
                                        combobox.updateSelectedOptionIndex();
                                        setSearch(event.currentTarget.value);
                                    }}
                                    onFocus={() => combobox.openDropdown()}
                                    onBlur={() => combobox.closeDropdown()}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Backspace' && search.length === 0) {
                                            event.preventDefault();
                                            if (value.length > 0) 
                                                handleValueRemove(value[value.length - 1].toString());
                                            
                                        }
                                    }}
                                />
                            </Combobox.EventsTarget>
                        </Pill.Group>
                    </PillsInput>
                </Combobox.DropdownTarget>

                <Combobox.Dropdown>
                    <Combobox.Options>
                        {options.length > 0 ? (
                            options
                        ) : (
                            <Combobox.Empty>{t('formHcf.noTherapeuticIndicationsFound')}</Combobox.Empty>
                        )}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        );
    }),
);

export default TherapeuticIndicationMultiSelect;
